import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { osFetch } from "../api";
import { useOS } from "../OSContext";
import { AIPanel, ConnectedPanel, ChangedPanel, Card } from "../shared/panels";
import { ArrowLeft, Loader2, Boxes, Archive, Save, Target } from "lucide-react";
import { toast } from "sonner";

const LIFECYCLES = ["idea", "incubating", "live", "sunset"];
const HEALTH = ["green", "yellow", "red", "unknown"];
const HEALTH_COLOR = { green: "bg-emerald-500", yellow: "bg-amber-500", red: "bg-rose-500", unknown: "bg-slate-600" };

export default function ProductDetail() {
  const { id } = useParams();
  const { can } = useOS();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => osFetch(`/catalog/${id}`).then((d) => { setData(d); setEdit(null); }).catch((e) => toast.error(e.message)), [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex items-center gap-2 text-slate-400 py-20"><Loader2 className="animate-spin" /> Loading…</div>;
  const p = data.entity;

  const save = async () => {
    setSaving(true);
    try { await osFetch(`/catalog/${id}`, { method: "PATCH", body: { ...edit, expected_version: p.current_version } }); toast.success("Saved"); load(); }
    catch (e) { toast.error(e.status === 409 ? "Someone else edited this — reloading." : e.message); if (e.status === 409) load(); }
    finally { setSaving(false); }
  };
  const archive = async () => { if (!window.confirm("Archive this product?")) return; await osFetch(`/catalog/${id}/archive`, { method: "POST" }); toast.success("Archived"); navigate("/os/catalog"); };
  const onAI = (ai) => setData({ ...data, entity: { ...p, ai } });

  const e = edit || {};
  return (
    <div className="max-w-6xl" data-testid="os-product-detail">
      <Link to="/os/catalog" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-4" data-testid="os-product-back"><ArrowLeft size={14} /> Product Catalog</Link>

      {/* Header — What */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <Boxes className="text-[#00F0FF]" />
            {edit ? <input value={e.name} onChange={(ev) => setEdit({ ...e, name: ev.target.value })} className="os-input text-2xl font-bold" data-testid="os-edit-name" />
              : <h1 className="font-display font-extrabold text-3xl truncate" data-testid="os-product-name">{p.name}</h1>}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <span className={`w-2.5 h-2.5 rounded-full ${HEALTH_COLOR[p.health]}`} />
            <span className="text-xs font-mono uppercase tracking-widest text-slate-400">{p.lifecycle}</span>
            <span className="text-xs font-mono text-slate-600">· v{p.current_version}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {can("update") && !edit && <button onClick={() => setEdit({ name: p.name, description: p.description, why: p.why, lifecycle: p.lifecycle, health: p.health, north_star: p.north_star })} className="border border-white/15 px-4 py-2.5 rounded-xl text-sm hover:border-white/30" data-testid="os-product-edit">Edit</button>}
          {edit && <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#0055FF] px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="os-product-save">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>}
          {edit && <button onClick={() => setEdit(null)} className="border border-white/15 px-4 py-2.5 rounded-xl text-sm">Cancel</button>}
          {can("delete") && !edit && <button onClick={archive} className="border border-rose-500/30 text-rose-300 px-3 py-2.5 rounded-xl text-sm hover:bg-rose-500/10" data-testid="os-product-archive"><Archive size={14} /></button>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-8">
        {/* Left: What + Why */}
        <div className="lg:col-span-2 space-y-4">
          <Card title="What is this?" testid="os-what">
            {edit ? <textarea value={e.description} onChange={(ev) => setEdit({ ...e, description: ev.target.value })} rows={3} className="os-input" /> : <p className="text-slate-300">{p.description || "—"}</p>}
          </Card>
          <Card title="Why does it exist?" testid="os-why">
            {edit ? <textarea value={e.why} onChange={(ev) => setEdit({ ...e, why: ev.target.value })} rows={3} className="os-input" /> : <p className="text-slate-300">{p.why || "—"}</p>}
          </Card>
          <Card icon={Target} title="North-star metric" testid="os-northstar">
            {edit ? <input value={e.north_star} onChange={(ev) => setEdit({ ...e, north_star: ev.target.value })} className="os-input" /> : <p className="text-slate-300">{p.north_star || "—"}</p>}
          </Card>
          {edit && (
            <div className="grid grid-cols-2 gap-3">
              <Card title="Lifecycle"><select value={e.lifecycle} onChange={(ev) => setEdit({ ...e, lifecycle: ev.target.value })} className="os-input">{LIFECYCLES.map((l) => <option key={l}>{l}</option>)}</select></Card>
              <Card title="Health"><select value={e.health} onChange={(ev) => setEdit({ ...e, health: ev.target.value })} className="os-input">{HEALTH.map((h) => <option key={h}>{h}</option>)}</select></Card>
            </div>
          )}
          <AIPanel entityType="product" entityId={id} base="/catalog" ai={p.ai} onUpdate={onAI} />
        </div>

        {/* Right: Connected + Changed */}
        <div className="space-y-4">
          <ConnectedPanel backlinks={data.backlinks} links={data.links} />
          <ChangedPanel activity={data.activity} history={data.history} base="/catalog" entityId={id} onRestored={load} />
        </div>
      </div>
    </div>
  );
}
